import React, { useState } from 'react';
import './LegalLinksManagement.css';

/**
 * ⚖️ LEGAL LINKS MANAGEMENT COMPONENT 2025
 * 
 * Toggle-Sektion für Fancy Footer Legal Links nach Design-Guide:
 * - 1:1 Text-Edit-Design
 * - Legal Links (Privacy, Terms, Impressum, etc.)
 * - Add/Remove-Funktionalität
 * - Spezifische Klassennamen für Legal Links
 */
function LegalLinksManagement({
    legalLinks,
    addLegalLink,
    removeLegalLink,
    updateLegalLink,
    editData
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Legal Link Types
    const legalTypes = [
        { value: 'privacy', label: 'Privacy Policy', icon: '🛡️' },
        { value: 'terms', label: 'Terms of Service', icon: '📋' },
        { value: 'impressum', label: 'Impressum', icon: '📄' },
        { value: 'cookies', label: 'Cookie Policy', icon: '🍪' },
        { value: 'disclaimer', label: 'Disclaimer', icon: '⚠️' },
        { value: 'contact', label: 'Contact', icon: '📧' },
        { value: 'about', label: 'About Us', icon: 'ℹ️' },
        { value: 'custom', label: 'Custom Link', icon: '🔗' }
    ];

    return (
        <div className="legal-links-management">
            {/* Toggle Header */}
            <div 
                className={`legal-links-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="legal-links-header-content">
                    <div className="legal-links-header-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1L12 5v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                            <path d="M8 1v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <h3 className="legal-links-header-title">Legal Links</h3>
                    <div className="legal-links-counter">
                        {legalLinks?.length || 0}
                    </div>
                </div>
                <div className={`legal-links-toggle-arrow ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="legal-links-content">
                    {/* Add Legal Link Button */}
                    <div className="add-legal-section">
                        <button onClick={addLegalLink} className="add-legal-btn">
                            <div className="btn-icon">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <span>Add Legal Link</span>
                        </button>
                    </div>

                    {/* Legal Links Grid */}
                    {legalLinks && legalLinks.length > 0 && (
                        <div className="legal-links-grid">
                            {legalLinks.map((link, index) => {
                                const linkType = legalTypes.find(t => t.value === link.type) || legalTypes[7]; // Default to custom
                                
                                return (
                                    <div key={link.id || index} className="legal-link-edit-card">
                                        <div className="legal-link-edit-content">
                                            <div className="legal-link-edit-header">
                                                <div className="legal-link-type-icon">
                                                    <span>{linkType.icon}</span>
                                                </div>
                                                <button
                                                    onClick={() => removeLegalLink(index)}
                                                    className="legal-link-remove-btn"
                                                    title="Remove Legal Link"
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                            </div>

                                            <div className="legal-link-inputs">
                                                {/* Type Selector */}
                                                <select
                                                    value={link.type || 'custom'}
                                                    onChange={(e) => updateLegalLink(index, 'type', e.target.value)}
                                                    className="legal-type-select"
                                                >
                                                    {legalTypes.map(type => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.icon} {type.label}
                                                        </option>
                                                    ))}
                                                </select>

                                                {/* Text Input */}
                                                <input
                                                    type="text"
                                                    value={link.text || ''}
                                                    onChange={(e) => updateLegalLink(index, 'text', e.target.value)}
                                                    placeholder="Link Text"
                                                    className="legal-text-input"
                                                />

                                                {/* URL Input */}
                                                <input
                                                    type="url"
                                                    value={link.url || ''}
                                                    onChange={(e) => updateLegalLink(index, 'url', e.target.value)}
                                                    placeholder="https://example.com/privacy"
                                                    className="legal-url-input"
                                                />
                                            </div>

                                            {/* Legal Link Preview */}
                                            <div className="legal-link-preview">
                                                <div className="legal-preview-icon">
                                                    <span>{linkType.icon}</span>
                                                </div>
                                                <span className="legal-preview-text">
                                                    {link.text || linkType.label}: {link.url || 'Enter URL...'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Empty State */}
                    {(!legalLinks || legalLinks.length === 0) && (
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <path d="M24 3L36 15v24a6 6 0 0 1-6 6H18a6 6 0 0 1-6-6V15L24 3Z" stroke="#d1d5db" strokeWidth="2" strokeLinejoin="round"/>
                                    <path d="M24 3v12h12" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <h4>No Legal Links Yet</h4>
                            <p>Add legal links like Privacy Policy, Terms of Service, Impressum, etc.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default LegalLinksManagement;
